/**
 * UPLOAD PAYSTUB STEP - Full extraction from uploaded paystubs
 * 
 * Features:
 * - Upload PDF or image of existing paystub
 * - Uses AI (Gemini Vision) to extract ALL data
 * - Auto-fills the wizard form with extracted data
 * - Supports both "Use with Template" and "Create Replica" modes
 */

import { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { 
  Upload, 
  FileText, 
  ArrowLeft, 
  Loader2, 
  CheckCircle2, 
  Building2,
  User,
  DollarSign,
  Calendar,
  AlertCircle,
  Sparkles,
  Copy,
  Image
} from 'lucide-react';
import axios from 'axios';
import { normFreq, PERIODS } from '../../lib/paystubRedate';

const API = process.env.REACT_APP_BACKEND_URL;

export const extractedToFormData = (extractedData) => {
  const formData = {
    // Company Info
    companyName: extractedData.company?.name || '',
    companyAddress1: extractedData.company?.address || '',
    companyCity: extractedData.company?.city || '',
    companyState: extractedData.company?.state || '',
    companyZip: extractedData.company?.zip || '',
    companyPhone: extractedData.company?.phone || '',
    companyEIN: extractedData.company?.ein || '',
    
    // Employee Info
    employeeName: extractedData.employee?.name || '',
    employeeAddress1: extractedData.employee?.address || '',
    employeeCity: extractedData.employee?.city || '',
    employeeState: extractedData.employee?.state || '',
    employeeZip: extractedData.employee?.zip || '',
    employeeSSN: extractedData.employee?.ssn_last4 || '',
    employeeId: extractedData.employee?.employee_id || '',
    
    // Pay Info
    payFrequency: normFreq(extractedData.pay_info?.pay_frequency),
    
    // Employment Details
    hourlyRate: extractedData.earnings?.hourly_rate || '',
    hoursPerPeriod: extractedData.earnings?.hours_worked || 80,
    paymentType: extractedData.earnings?.hourly_rate ? 'hourly' : 'salary',
    annualSalary: Math.round((extractedData.earnings?.gross_pay || 0) * (PERIODS[normFreq(extractedData.pay_info?.pay_frequency)] || 26) * 100) / 100 || 52000,
    taxState: extractedData.company?.state || '',
    
    // Create paystub with extracted data
    paystubs: [{
      payDate: extractedData.pay_info?.pay_date || '',
      periodStart: extractedData.pay_info?.period_start || '',
      periodEnd: extractedData.pay_info?.period_end || '',
      checkNumber: extractedData.pay_info?.check_number || '',
      current: {
        hours: extractedData.earnings?.hours_worked || 80,
        rate: extractedData.earnings?.hourly_rate || 0,
        regular: extractedData.earnings?.regular_pay || 0,
        overtimeHours: extractedData.earnings?.overtime_hours || 0,
        overtime: extractedData.earnings?.overtime_pay || 0,
        bonus: extractedData.earnings?.bonus || 0,
        grossPay: extractedData.earnings?.gross_pay || 0,
        federalTax: extractedData.deductions?.federal_tax || 0,
        stateTax: extractedData.deductions?.state_tax || 0,
        socialSecurity: extractedData.deductions?.social_security || 0,
        medicare: extractedData.deductions?.medicare || 0,
        otherDeductions: extractedData.deductions?.other_deductions || 0,
        netPay: extractedData.net_pay || 0
      },
      ytd: {
        grossPay: extractedData.ytd?.gross_pay || 0,
        federalTax: extractedData.ytd?.federal_tax || 0,
        stateTax: extractedData.ytd?.state_tax || 0,
        socialSecurity: extractedData.ytd?.social_security || 0,
        medicare: extractedData.ytd?.medicare || 0,
        netPay: extractedData.ytd?.net_pay || 0
      }
    }],
    numberOfStubs: 1
  };
  return formData;
};

export default function UploadPaystubStep({ onExtracted, onCancel, updateMode = false }) {
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    setError(null);
    setExtractedData(null);
    
    // Create preview for images
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(selectedFile);
    } else {
      setFilePreview(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setProcessing(true);
    setError(null);
    
    try {
      // Convert file to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      
      // Determine file type
      const fileType = file.type.includes('pdf') ? 'pdf' : 'image';
      
      toast.info('Analyzing paystub with AI...', { duration: 5000 });
      
      // Call the extraction API
      const response = await axios.post(`${API}/api/paystub/extract`, {
        image_base64: base64,
        file_type: fileType
      });
      
      if (response.data.success) {
        const data = response.data.extracted_data;
        setExtractedData(data);
        toast.success('Successfully extracted paystub data!');
      } else {
        throw new Error(response.data.error || 'Extraction failed');
      }
    } catch (err) {
      console.error('Extraction error:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to extract data from paystub');
      toast.error('Failed to extract data. Please try a clearer image.');
    } finally {
      setProcessing(false);
    }
  };

  const handleUseWithTemplate = () => {
    if (!extractedData) return;
    onExtracted({ mode: 'template', formData: extractedToFormData(extractedData) });
  };

  const handleContinueUpdate = () => {
    if (!extractedData) return;
    onExtracted({ mode: 'update', formData: extractedToFormData(extractedData), extractedData });
  };

  const handleCreateReplica = () => {
    if (!extractedData) return;
    
    // For replica, we pass all the extracted data including layout info
    onExtracted({
      mode: 'replica',
      extractedData: extractedData,
      formData: {
        companyName: extractedData.company?.name || '',
        employeeName: extractedData.employee?.name || '',
      }
    });
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return `$${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={onCancel} className="mb-2" data-testid="upload-back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Client Selection
      </Button>
      
      <Card className="bg-zinc-900 border-zinc-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-3">
            <Upload className="w-6 h-6 text-purple-400" />
            {updateMode ? 'Update an Existing Paystub' : 'Upload Existing Paystub'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Upload Area */}
          <div 
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
              file ? 'border-purple-500 bg-purple-500/10' : 'border-zinc-700 hover:border-zinc-600'
            }`}
            onClick={() => fileInputRef.current?.click()}
            style={{ cursor: 'pointer' }}
          >
            {filePreview ? (
              <img 
                src={filePreview} 
                alt="Preview" 
                className="max-h-48 mx-auto mb-4 rounded-lg"
              />
            ) : (
              <div className="mb-4">
                {file ? (
                  <FileText className="h-16 w-16 mx-auto text-purple-400" />
                ) : (
                  <Upload className="h-16 w-16 mx-auto text-zinc-500" />
                )}
              </div>
            )}
            
            <p className="text-zinc-400 mb-2">
              {file ? file.name : 'Click to upload or drag and drop'}
            </p>
            <p className="text-zinc-500 text-sm">
              PDF, PNG, JPG or JPEG (max 10MB)
            </p>
            
            <input
              ref={fileInputRef}
              data-testid="upload-paystub-file-input"
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
          
          {/* Extract Button */}
          {file && !extractedData && (
            <Button
              onClick={handleUpload}
              disabled={processing}
              data-testid="upload-extract-btn"
              className="w-full bg-purple-600 hover:bg-purple-700 h-14 text-lg"
            >
              {processing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Extracting Data with AI...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Extract All Data
                </>
              )}
            </Button>
          )}
          
          {/* Error Display */}
          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
              <div>
                <p className="text-red-400 font-medium">Extraction Failed</p>
                <p className="text-sm text-red-300/70">{error}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Extracted Data Preview */}
      {extractedData && (
        <Card className="bg-zinc-900 border-green-500/30">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-400" />
              Data Extracted Successfully
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Company Info */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-4 bg-zinc-800 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-5 h-5 text-blue-400" />
                  <h3 className="font-semibold text-white">Company</h3>
                </div>
                <div className="space-y-1 text-sm">
                  <p className="text-white font-medium">{extractedData.company?.name || 'N/A'}</p>
                  <p className="text-zinc-400">{extractedData.company?.address}</p>
                  <p className="text-zinc-400">
                    {extractedData.company?.city}, {extractedData.company?.state} {extractedData.company?.zip}
                  </p>
                  {extractedData.company?.phone && (
                    <p className="text-zinc-400">{extractedData.company.phone}</p>
                  )}
                </div>
              </div>
              
              <div className="p-4 bg-zinc-800 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-5 h-5 text-green-400" />
                  <h3 className="font-semibold text-white">Employee</h3>
                </div>
                <div className="space-y-1 text-sm">
                  <p className="text-white font-medium">{extractedData.employee?.name || 'N/A'}</p>
                  <p className="text-zinc-400">{extractedData.employee?.address}</p>
                  <p className="text-zinc-400">
                    {extractedData.employee?.city}, {extractedData.employee?.state} {extractedData.employee?.zip}
                  </p>
                  <p className="text-zinc-400">SSN: XXX-XX-{extractedData.employee?.ssn_last4 || '****'}</p>
                </div>
              </div>
            </div>
            
            {/* Pay Info */}
            <div className="p-4 bg-zinc-800 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-5 h-5 text-amber-400" />
                <h3 className="font-semibold text-white">Pay Period</h3>
              </div>
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-zinc-500">Pay Date</p>
                  <p className="text-white">{extractedData.pay_info?.pay_date || '-'}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Period Start</p>
                  <p className="text-white">{extractedData.pay_info?.period_start || '-'}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Period End</p>
                  <p className="text-white">{extractedData.pay_info?.period_end || '-'}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Frequency</p>
                  <p className="text-white capitalize">{extractedData.pay_info?.pay_frequency || '-'}</p>
                </div>
              </div>
            </div>
            
            {/* Earnings & Deductions */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-4 bg-zinc-800 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  <h3 className="font-semibold text-white">Earnings</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Regular Pay</span>
                    <span className="text-white">{formatCurrency(extractedData.earnings?.regular_pay)}</span>
                  </div>
                  {extractedData.earnings?.overtime_pay > 0 && (
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Overtime</span>
                      <span className="text-white">{formatCurrency(extractedData.earnings?.overtime_pay)}</span>
                    </div>
                  )}
                  {extractedData.earnings?.bonus > 0 && (
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Bonus</span>
                      <span className="text-white">{formatCurrency(extractedData.earnings?.bonus)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-zinc-700 pt-2 font-medium">
                    <span className="text-white">Gross Pay</span>
                    <span className="text-green-400">{formatCurrency(extractedData.earnings?.gross_pay)}</span>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-zinc-800 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-5 h-5 text-red-400" />
                  <h3 className="font-semibold text-white">Deductions</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Federal Tax</span>
                    <span className="text-white">{formatCurrency(extractedData.deductions?.federal_tax)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">State Tax</span>
                    <span className="text-white">{formatCurrency(extractedData.deductions?.state_tax)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Social Security</span>
                    <span className="text-white">{formatCurrency(extractedData.deductions?.social_security)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Medicare</span>
                    <span className="text-white">{formatCurrency(extractedData.deductions?.medicare)}</span>
                  </div>
                  <div className="flex justify-between border-t border-zinc-700 pt-2 font-medium">
                    <span className="text-white">Total Deductions</span>
                    <span className="text-red-400">{formatCurrency(extractedData.deductions?.total_deductions)}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Net Pay */}
            <div className="p-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-lg text-center">
              <p className="text-zinc-400 mb-1">Net Pay</p>
              <p className="text-4xl font-bold text-green-400">{formatCurrency(extractedData.net_pay)}</p>
            </div>
            
            {/* Action Buttons */}
            {updateMode ? (
              <Button
                onClick={handleContinueUpdate}
                className="w-full h-14 bg-gradient-to-r from-sky-600 to-cyan-600 hover:from-sky-700 hover:to-cyan-700 text-lg"
                data-testid="upload-continue-update-btn"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Continue — Choose New Dates
              </Button>
            ) : (
            <div className="grid md:grid-cols-2 gap-4">
              <Button
                onClick={handleUseWithTemplate}
                className="h-14 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-lg"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Use Data with Template
              </Button>
              <Button
                onClick={handleCreateReplica}
                variant="outline"
                className="h-14 border-purple-500 text-purple-400 hover:bg-purple-500/10 text-lg"
              >
                <Copy className="w-5 h-5 mr-2" />
                Create Exact Replica
              </Button>
            </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
